'use client'

import jsCookie from 'js-cookie'
import {createContext, FC, useContext, useEffect, useState} from 'react'

import {Affiliate} from '@/utils/affiliate/config'
import {getCurrentParams, removeCurrentParams} from '@/utils/browser/location'
import {
  affiliateCookieName,
  affiliateParamName,
  countrySpecificParamName,
  currencyCookieName,
  currencyParamName,
  destinationCookieName,
  destinationParamName,
  originCookieName,
  originParamName,
  visitorCookieValidityInDays,
} from '@/utils/constants/http'
import {Domain, Region, validateRegion} from '@/utils/countries'
import {Currency} from '@/utils/currency'
import {useNavigation} from '@/utils/hooks/useNavigation'
import {readClientCookieRecord} from '@/utils/preferences/cookies'
import {getVisitorCountry} from '@/utils/preferences/country'
import {readDebugOverrides} from '@/utils/preferences/debug'
import {PreferenceSources, resolvePreferences} from '@/utils/preferences/resolve'
import {CookieRecord} from '@/utils/preferences/types'
import {getDetectedTimezone} from '@/utils/timezone'

import {isDevelopment} from '../../common'

type PreferencesContextProps = {
  host: string
  cookies: CookieRecord
  params: Record<string, string | undefined>
  // From the request on a server render; resolved in the browser under `resolveOnClient`
  country?: string
  /**
   * Resolve preferences in the browser instead of trusting the props.
   *
   * A statically rendered page has no request behind it: the host is unknown (one prerendered
   * document is served to tramatm.com, .co.uk, .cz and the rest alike) and no cookies were read.
   * In that mode the first render uses the defaults, an effect re-resolves from
   * `window.location`, `document.cookie` and the visitor's country, and `isPreferencesPending` stays
   * true until it has - a guess is never shown, nor stored.
   */
  resolveOnClient?: boolean
}

type PreferencesContextValue = {
  affiliate?: Affiliate | null
  host: string
  country: string | undefined
  domain: Domain
  setDomain: (domain: Domain) => void
  currency: Currency
  setCurrency: (currency: Currency) => void
  origin: Region
  destination: Region | null
  setDestination: (destination: Region | null) => void
  setAffiliate: (affiliate: Affiliate | null) => void
  timezone: string
  setTimezone: (timezone: string) => void
  // True while the values below are still the defaults, under `resolveOnClient` until the browser
  // sources have been read and adopted. Consumers that render preference-derived text show a
  // placeholder while it is set.
  isPreferencesPending: boolean
}

const emptySetFunction = () => {
  throw Error('Error: Preferences context was used prior to initialization')
}

const PreferencesContext = createContext<PreferencesContextValue>({
  affiliate: null,
  host: 'https://www.tramatm.com',
  country: undefined,
  domain: Domain.COM,
  setDomain: emptySetFunction,
  currency: 'EUR',
  setCurrency: emptySetFunction,
  origin: Region.EU,
  destination: null,
  setDestination: emptySetFunction,
  setAffiliate: emptySetFunction,
  timezone: 'Europe/Bratislava',
  setTimezone: emptySetFunction,
  isPreferencesPending: false,
})

export const usePreferences = () => useContext(PreferencesContext)

export const PreferencesProvider: FC<React.PropsWithChildren<PreferencesContextProps>> = ({
  children,
  host,
  cookies,
  params,
  country: requestCountry,
  resolveOnClient = false,
}) => {
  const {replace} = useNavigation()

  // Populated by the effect below, and only under `resolveOnClient`. Null means "not read yet".
  const [clientSources, setClientSources] = useState<PreferenceSources | null>(null)

  useEffect(() => {
    if (!resolveOnClient) {
      return
    }
    const resolve = async () => {
      const visitorCountry = await getVisitorCountry()
      setClientSources({
        host: readDebugOverrides().host ?? window.location.host,
        cookies: readClientCookieRecord(),
        params: getCurrentParams(),
        country: visitorCountry,
      })
    }
    void resolve()
  }, [resolveOnClient])

  const hostToUse = clientSources?.host ?? host
  const {
    country,
    origin: originValue,
    destination: destinationValue,
    currency: currencyValue,
    affiliate: affiliateValue,
    domain,
  } = resolvePreferences({
    host: hostToUse,
    cookies: clientSources?.cookies ?? cookies,
    params: clientSources?.params ?? params,
    country: clientSources ? clientSources.country : requestCountry,
  })

  const [currency, setCurrencyState] = useState<Currency>(currencyValue)
  const [origin, setOriginState] = useState<Region>(originValue)
  const [destination, setDestinationState] = useState<Region | null>(destinationValue)
  const [affiliate, setAffiliateState] = useState<Affiliate | null>(affiliateValue)
  const [timezone, setTimezone] = useState<string>(getDetectedTimezone())

  const setDomain = (domain: Domain) => {
    if (!isDevelopment) {
      // Click handler, so the live URL is authoritative — and this keeps the provider, which
      // wraps the whole app, from subscribing to search params just to carry the query string
      // across a domain switch. The path keeps its locale prefix: the URL decides the language,
      // and the other domain has no cookie of this one to go by. A link's region does not go along:
      // picking a domain is picking its region.
      removeCurrentParams([originParamName])
      replace(`${domain}${window.location.pathname}${window.location.search}`)
    }
  }

  // What a link's params set is stored as it is; the address keeps the link
  const storeCurrency = (currency: Currency) => {
    setCurrencyState(currency)
    jsCookie.set(currencyCookieName, currency, {expires: visitorCookieValidityInDays})
  }

  const storeOrigin = (origin: Region) => {
    setOriginState(origin)
    jsCookie.set(originCookieName, origin, {expires: visitorCookieValidityInDays})
  }

  const storeDestination = (destination: Region | null) => {
    setDestinationState(destination)
    if (!destination) {
      jsCookie.remove(destinationCookieName)
    } else {
      jsCookie.set(destinationCookieName, destination)
    }
  }

  const storeAffiliate = (affiliate: Affiliate | null) => {
    setAffiliateState(affiliate)
    if (!affiliate) {
      jsCookie.remove(affiliateCookieName)
    } else {
      jsCookie.set(affiliateCookieName, affiliate, {expires: 30})
    }
  }

  // The setters the page calls are explicit choices, which replace what a link set - so its param
  // leaves the address too (docs/visitor-preferences.md)
  const setCurrency = (currency: Currency) => {
    storeCurrency(currency)
    removeCurrentParams([currencyParamName])
  }

  const setDestination = (destination: Region | null) => {
    storeDestination(destination)
    removeCurrentParams([destinationParamName, countrySpecificParamName])
  }

  const setAffiliate = (affiliate: Affiliate | null) => {
    storeAffiliate(affiliate)
    removeCurrentParams([affiliateParamName])
  }

  const [hasAdoptedClientSources, setHasAdoptedClientSources] = useState(!resolveOnClient)

  // The state above was initialised from whatever `resolvePreferences` returned on the very first
  // render. Under `resolveOnClient` that was the defaults, so adopt the real values once the
  // browser sources arrive. Uses the raw setters — the effect below stores what should be kept.
  useEffect(() => {
    if (!clientSources) {
      return
    }
    setCurrencyState(currencyValue)
    setOriginState(originValue)
    setDestinationState(destinationValue)
    setAffiliateState(affiliateValue)
    setHasAdoptedClientSources(true)
  }, [clientSources, currencyValue, originValue, destinationValue, affiliateValue])

  // Not as soon as the sources are read: until the effect above adopts them, the state still holds
  // the defaults, and consumers' effects (which run first) would act on those
  const isPreferencesPending = !hasAdoptedClientSources

  const cookieSource = clientSources?.cookies ?? cookies
  const paramSource = clientSources?.params ?? params
  const currencyCookieValue = cookieSource[currencyCookieName]
  const originCookieValue = cookieSource[originCookieName]
  const destinationCookieValue = cookieSource[destinationCookieName] ?? null
  const affiliateCookieValue = cookieSource[affiliateCookieName] ?? null
  const isCurrencyFromParam = paramSource[currencyParamName] === currencyValue
  const isOriginFromParam = validateRegion(paramSource[originParamName]) !== undefined

  useEffect(() => {
    // Only what the visitor gave is stored (docs/visitor-preferences.md). A campaign link's
    // currency or region is a choice to keep; one derived from the country or the domain is
    // recomputed on every page, and stored it would outrank better inputs later.
    if (isCurrencyFromParam && currencyCookieValue !== currencyValue) {
      storeCurrency(currencyValue)
    }
    if (isOriginFromParam && originCookieValue !== originValue) {
      storeOrigin(originValue)
    }
    if (destinationCookieValue !== destinationValue) {
      storeDestination(destinationValue)
    }
    if (affiliateCookieValue !== affiliateValue) {
      storeAffiliate(affiliateValue)
    }
  }, [
    isCurrencyFromParam,
    currencyCookieValue,
    currencyValue,
    isOriginFromParam,
    originCookieValue,
    originValue,
    destinationCookieValue,
    destinationValue,
    affiliateValue,
    affiliateCookieValue,
  ])

  return (
    <PreferencesContext.Provider
      value={{
        affiliate,
        host: hostToUse,
        country,
        domain,
        setDomain,
        currency,
        setCurrency,
        origin,
        destination,
        setDestination,
        setAffiliate,
        timezone,
        setTimezone,
        isPreferencesPending,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  )
}
